import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Users, Check } from 'lucide-react';
import { api, type ApiError } from '../../api';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { cn } from '../../lib/utils';

interface Pres { id: string; code: string | null; type: 'oral' | 'poster'; title: string }
interface Jdg { id: string; name: string }

const key = (p: string, j: string) => `${p}::${j}`;

export default function ManageAllocations({ yearId }: { yearId: number }) {
  const [presentations, setPresentations] = useState<Pres[]>([]);
  const [judges, setJudges] = useState<Jdg[]>([]);
  const [assigned, setAssigned] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    setLoading(true);
    api<{ presentations: Pres[]; judges: Jdg[]; allocations: { presentationId: string; judgeId: string }[] }>(
      `admin-allocations?yearId=${yearId}`,
    )
      .then((d) => {
        setPresentations(d.presentations);
        setJudges(d.judges);
        setAssigned(new Set(d.allocations.map((a) => key(a.presentationId, a.judgeId))));
      })
      .catch((e) => toast.error((e as ApiError).message || 'Failed to load allocations'))
      .finally(() => setLoading(false));
  }, [yearId]);

  async function toggle(presId: string, judgeId: string) {
    const k = key(presId, judgeId);
    const nowAssigned = !assigned.has(k);
    setAssigned((prev) => {
      const next = new Set(prev);
      nowAssigned ? next.add(k) : next.delete(k);
      return next;
    });
    try {
      await api('admin-allocations', { body: { presentationId: presId, judgeId, assigned: nowAssigned } });
    } catch (e) {
      // revert on failure
      setAssigned((prev) => {
        const next = new Set(prev);
        nowAssigned ? next.delete(k) : next.add(k);
        return next;
      });
      toast.error((e as ApiError).message || 'Could not update allocation');
    }
  }

  const q = query.trim().toLowerCase();
  const filtered = q
    ? presentations.filter((p) => p.title.toLowerCase().includes(q) || (p.code ?? '').toLowerCase().includes(q))
    : presentations;

  if (loading)
    return (
      <div className="space-y-2">
        <Skeleton className="h-14 w-full rounded-lg" />
        <Skeleton className="h-14 w-full rounded-lg" />
        <Skeleton className="h-14 w-full rounded-lg" />
      </div>
    );

  if (judges.length === 0)
    return <p className="py-8 text-center text-muted-foreground">Add judges to this year first, then assign them here.</p>;

  return (
    <div className="space-y-3">
      <Input placeholder="Search presentations…" value={query} onChange={(e) => setQuery(e.target.value)} />
      <p className="text-xs text-muted-foreground">Tap a presentation to assign judges. Changes save instantly.</p>
      <div className="space-y-2">
        {filtered.map((p) => {
          const open = expanded === p.id;
          const count = judges.filter((j) => assigned.has(key(p.id, j.id))).length;
          return (
            <Card key={p.id} className="overflow-hidden">
              <button onClick={() => setExpanded(open ? null : p.id)} className="flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-accent">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-muted-foreground">{p.code ?? p.id}</span>
                    <Badge variant={p.type === 'oral' ? 'oral' : 'poster'} className="capitalize">{p.type}</Badge>
                  </div>
                  <div className="line-clamp-1 text-sm font-medium">{p.title}</div>
                </div>
                <Badge variant={count > 0 ? 'default' : 'secondary'}>
                  <Users className="size-3" /> {count}
                </Badge>
                {open ? <ChevronDown className="size-4 text-muted-foreground" /> : <ChevronRight className="size-4 text-muted-foreground" />}
              </button>
              {open && (
                <div className="grid grid-cols-2 gap-2 border-t bg-muted/30 p-3 sm:grid-cols-3">
                  {judges.map((j) => {
                    const on = assigned.has(key(p.id, j.id));
                    return (
                      <button
                        key={j.id}
                        onClick={() => toggle(p.id, j.id)}
                        className={cn(
                          'flex items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors',
                          on ? 'border-primary bg-primary/10 font-medium text-primary' : 'border-input bg-card hover:bg-accent',
                        )}
                      >
                        <span className={cn('flex size-4 shrink-0 items-center justify-center rounded border', on ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40')}>
                          {on && <Check className="size-3" />}
                        </span>
                        <span className="line-clamp-1">{j.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </Card>
          );
        })}
        {filtered.length === 0 && <p className="py-8 text-center text-muted-foreground">No presentations match.</p>}
      </div>
    </div>
  );
}
